import { useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import {
  Box,
  Stack,
  Button,
  CircularProgress,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getResponseErrorMessage, useFlowBuilder2Store, useEditorStore } from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

export const SaveStatusIndicator = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const isSaving = useFlowBuilder2Store((s) => s.requests.some((r) => !r.error));
  const isSavingEditor = useEditorStore((s) => s.isSaving);
  const errors = useFlowBuilder2Store((s) => s.requests.filter((r) => r.error), isEqual);

  const content = useMemo(() => {
    if (isSaving || isSavingEditor)
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size="1em" />
          <Typography color="text.secondary">{t('labels.saving')}</Typography>
        </Stack>
      );

    if (errors.length)
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            startIcon={<ErrorIcon color="error" />}
            color="secondary"
            sx={({ palette }) => ({
              color: palette.text.primary,
            })}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            {t('labels.savingError')}
          </Button>
        </Stack>
      );
  }, [isSaving, isSavingEditor, errors, t]);

  return (
    <>
      {content}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ sx: ({ palette }) => ({ backgroundColor: palette.background.paper }) }}
      >
        <Box width="400px" maxWidth="90vw">
          <Box
            sx={({ palette }) => ({ px: 2, py: 1, borderBottom: `1px solid ${palette.divider}` })}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>{t('labels.savingError')}</Typography>
              <Button
                size="small"
                disabled={isSaving}
                onClick={() => {
                  setAnchorEl(null);
                  Promise.all(errors.map((e) => e.retry())).catch();
                }}
              >
                {t('buttons.retryAll')}
              </Button>
            </Stack>
          </Box>
          <List disablePadding>
            {errors.map((item) => (
              <ListItem key={item.id} divider>
                <ListItemText
                  primaryTypographyProps={{ color: 'error' }}
                  primary={getResponseErrorMessage(item.error)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};
